import React, { useRef, useState, useEffect } from 'react';
import { QRCodeCanvas } from 'qrcode.react';

const EntryPassModal = ({ show, attendee, project, onClose }) => {
  const canvasRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [shareStatus, setShareStatus] = useState(null);
  const [qrReady, setQrReady] = useState(false);
  const [supportStatus, setSupportStatus] = useState({
    share: false,
    clipboardImage: false,
    checked: false
  });

  useEffect(() => {
    if (show) {
      setQrReady(false);
      setCopied(false);
      setShareStatus(null);
      
      const checkSupport = async () => {
        const shareSupported = !!(navigator.share && navigator.canShare);
        
        let clipboardSupported = false;
        try {
          clipboardSupported = !!(
            navigator.clipboard && 
            navigator.clipboard.write && 
            window.ClipboardItem &&
            window.ClipboardItem.supports?.('image/png')
          );
        } catch (e) {
          // Silent fail
        }
        
        setSupportStatus({
          share: shareSupported,
          clipboardImage: clipboardSupported,
          checked: true
        });
      };
      
      checkSupport();
      
      const timer = setTimeout(() => {
        setQrReady(true);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [show]);

  if (!show || !attendee) return null;

  const qrValue = attendee.qrCode || `ATT-${attendee.id}`;
  const qrUrl = `${window.location.origin}/access/${project?.id}?qr=${attendee.id}`;

  const canvasToFile = async (canvas, nombre = 'pase.png') => {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('No se pudo generar el blob del canvas'));
          return;
        }
        const file = new File([blob], nombre, { type: 'image/png' });
        resolve(file);
      }, 'image/png', 0.92);
    });
  };

  const handleCopyQR = async () => {
    try {
      const canvas = canvasRef.current?.querySelector('canvas');
      
      if (!canvas) {
        setShareStatus({ type: 'error', text: 'Canvas no encontrado' });
        return;
      }

      const blob = await new Promise((resolve) => {
        canvas.toBlob((b) => resolve(b), 'image/png');
      });

      if (!blob) {
        throw new Error('No se pudo generar el blob');
      }

      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ]);
      
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      setShareStatus({ type: 'error', text: 'Error al copiar. Usa el botón descargar.' });
      setTimeout(() => setShareStatus(null), 3000);
    }
  };

  const handleShare = async () => {
    const canvas = canvasRef.current?.querySelector('canvas');
    if (!canvas) {
      setShareStatus({ type: 'error', text: 'QR no listo' });
      return;
    }

    setShareStatus({ type: 'loading', text: 'Preparando imagen...' });

    try {
      const file = await canvasToFile(canvas, `pase-${attendee.fullName?.replace(/\s+/g, '-') || 'ingreso'}.png`);

      const shareData = { files: [file] };
      
      if (navigator.canShare && !navigator.canShare(shareData)) {
        throw new Error('No se puede compartir archivos en este navegador');
      }
      
      let textContent = `🎫 *Pase de Ingreso*\n\n`;
      textContent += `👤 *${attendee.fullName}*\n`;
      if (attendee.email) textContent += `📧 ${attendee.email}\n`;
      if (attendee.phone) textContent += `📱 ${attendee.phone}\n`;
      if (attendee.company) textContent += `🏢 ${attendee.company}`;

      await navigator.share({
        files: [file],
        title: `Pase de Ingreso - ${attendee.fullName}`,
        text: textContent
      });

      setShareStatus({ type: 'success', text: '¡Compartido!' });
      setTimeout(() => setShareStatus(null), 3000);
    } catch (err) {
      if (err.name === 'AbortError') {
        setShareStatus({ type: 'warning', text: 'Cancelado' });
      } else {
        setShareStatus({ type: 'error', text: err.message || 'Error al compartir' });
      }
      setTimeout(() => setShareStatus(null), 3000);
    }
  };

  const handleDownload = () => {
    const canvas = canvasRef.current?.querySelector('canvas');
    if (!canvas) return;

    const link = document.createElement('a');
    link.download = `pase-${attendee.fullName?.replace(/\s+/g, '-') || 'ingreso'}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const getStatusColor = () => {
    if (!shareStatus) return '';
    switch (shareStatus.type) {
      case 'success': return 'bg-green-100 text-green-800';
      case 'error': return 'bg-red-100 text-red-800';
      case 'warning': return 'bg-yellow-100 text-yellow-800';
      case 'loading': return 'bg-blue-100 text-blue-800';
      default: return '';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <h2 className="text-2xl font-bold text-center mb-4">Ficha de Ingreso</h2>
        <p className="text-center text-gray-600 mb-4">{project?.name}</p>
        
        <div className="flex justify-center mb-4" ref={canvasRef}>
          <QRCodeCanvas 
            value={qrValue} 
            size={200} 
            level={"H"} 
            includeMargin={true}
            renderAs="canvas"
          />
        </div>
        
        <div className="text-center">
          <p className="font-semibold">{attendee.fullName}</p>
          {attendee.email && <p className="text-gray-600">{attendee.email}</p>}
          {attendee.phone && <p className="text-gray-600">{attendee.phone}</p>}
          {attendee.company && <p className="text-gray-600">{attendee.company}</p>}
        </div>

        {copied && (
          <div className="mt-4 p-2 bg-green-100 text-green-800 rounded-lg text-center text-sm">
            ✓ Copiado al portapapeles
          </div>
        )}

        {shareStatus && (
          <div className={`mt-4 p-2 rounded-lg text-center text-sm ${getStatusColor()}`}>
            {shareStatus.text}
          </div>
        )}
        
        <div className="flex flex-col gap-2 mt-4">
          {supportStatus.share ? (
            <button 
              type="button"
              onClick={handleShare}
              disabled={!qrReady || shareStatus?.type === 'loading'}
              className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium disabled:bg-blue-300 flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              Compartir
            </button>
          ) : null}

          <div className="flex gap-2">
            <button 
              type="button"
              onClick={handleCopyQR}
              disabled={!qrReady}
              className="flex-1 px-4 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition font-medium disabled:bg-gray-300 disabled:text-gray-500 flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copiar
            </button>
            
            <button 
              type="button"
              onClick={handleDownload}
              disabled={!qrReady}
              className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-medium disabled:bg-purple-300 flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Descargar
            </button>
          </div>
          
          <button 
            type="button"
            onClick={onClose} 
            className="w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default EntryPassModal;
